/**
 * The words of Revisit Brand Setup's "Re-read my sources" (spec:127-139), Release 4. The banner's title,
 * sentence, buttons, legend and the hidden prefixes are the spec's own (spec:133, :139); the rest (a
 * re-read still running, one that found nothing, refusals and failures) is written here in the same voice.
 * No string here says "this brand" or "this company" (spec:28; wording.gate.test.ts).
 */
export const REVISIT_COPY = {
  // ── The banner after a re-read (spec:133) ──
  title: 'AI updated your brand details',
  body: 'Your wording is kept. AI made only minor edits based on your latest sources. Review and edit any field before saving.',
  undo: 'Undo and restore original text',
  regenerate: 'Regenerate',
  showChanges: 'Show changes',
  legendAdded: 'Added',
  legendRemoved: 'Removed',
  readOnlyNote: 'Fields are read-only while changes are shown.',
  close: 'Close',

  // ── Show changes: what a screen reader hears before a change (spec:139) ──
  addedPrefix: 'Added: ',
  removedPrefix: 'Removed: ',
  /** The tooltip of a change, as the composer's Show changes titles its marks. */
  addedTitle: 'Added',
  removedTitle: 'Removed',

  // ── Not in the spec ──
  /** While the re-read runs (its fields wait, read-only, for the proposal). */
  running: "AI is re-reading your sources. Fields are read-only until it's done.",
  /** The re-read proposed nothing that differs from your brand's saved details. */
  nothing: "AI re-read your sources and didn't find anything to update.",
  /** The re-read failed and the server gave no sentence of its own. */
  failed: "We couldn't finish re-reading your sources.",
  /** Still running after the client's ceiling (13 minutes). Try again looks again. */
  timedOut: "Re-reading your sources is taking longer than expected. Try again to check on it.",
  /** The proposal could not be read once the re-read had finished. */
  changesFailed: "We couldn't load the proposed changes.",
  tryAgain: 'Try again',
  startFailed: "Couldn't start re-reading your sources. Try again.",
  retryFailed: "Couldn't start re-reading your sources again. Try again.",
  regenerateFailed: "Couldn't regenerate the proposed changes. Try again.",
  undoFailed: "Couldn't restore your original text. Try again.",
  /** The toast once Undo has saved a step back: steps saved during the review hold the original text again. */
  restored: 'Original text restored',

  /** Undo after the person changed fields since the proposal arrived (the composer's rule). */
  undoConfirm: {
    title: 'Undo the changes?',
    message: 'Every field goes back to what was saved before the re-read. The changes you made since are lost, including any you saved.',
    confirmLabel: 'Undo and restore',
    cancelLabel: 'Cancel',
    danger: true,
  },
  /** Regenerate after the person changed fields since the proposal arrived. */
  regenerateConfirm: {
    title: 'Regenerate the changes?',
    message: 'The changes you made since the re-read are lost. AI reads your sources again and proposes new updates.',
    confirmLabel: 'Regenerate',
    cancelLabel: 'Cancel',
    danger: true,
  },
} as const;
